import { useState, useEffect, useCallback } from 'react'
import { clientService } from '../services/clientService'
import type { Client, ClientFilters } from '../types'

export function useClients(initialFilters?: Partial<ClientFilters>) {
  const [data, setData] = useState<Client[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<ClientFilters>({
    page: 1,
    limit: 20,
    ...initialFilters,
  })

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await clientService.getAll(filters)
      setData(response.data)
      setTotal(response.total_items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar clientes')
    } finally {
      setIsLoading(false)
    }
  }, [filters])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const setPage = (page: number) => setFilters((prev) => ({ ...prev, page }))
  const setLimit = (limit: number) => setFilters((prev) => ({ ...prev, limit, page: 1 }))

  const setSearch = (search?: string) =>
    setFilters((prev) => ({ ...prev, search, page: 1 }))
  const setCommune = (commune?: string) =>
    setFilters((prev) => ({ ...prev, commune, page: 1 }))
  const setGender = (gender?: string) =>
    setFilters((prev) => ({ ...prev, gender, page: 1 }))
  const setCategory = (last_known_category?: string) =>
    setFilters((prev) => ({ ...prev, last_known_category, page: 1 }))
  const setBranchOffice = (id_branch_office?: number) =>
    setFilters((prev) => ({ ...prev, id_branch_office, page: 1 }))
  const setSorting = (sort_by?: string, sort_order?: 'asc' | 'desc') =>
    setFilters((prev) => ({ ...prev, sort_by, sort_order }))

  const applyFilters = (partial: Partial<ClientFilters>) =>
    setFilters((prev) => ({ ...prev, ...partial, page: 1 }))

  const clearFilters = () =>
    setFilters({ page: 1, limit: filters.limit })

  const hasActiveFilters = !!(
    filters.search ||
    filters.commune ||
    filters.gender ||
    filters.last_known_category ||
    filters.id_branch_office !== undefined ||
    filters.created_from ||
    filters.created_to
  )

  return {
    data,
    total,
    isLoading,
    error,
    filters,
    hasActiveFilters,
    setPage,
    setLimit,
    setSearch,
    setCommune,
    setGender,
    setCategory,
    setBranchOffice,
    setSorting,
    applyFilters,
    clearFilters,
    refetch: fetchData,
  }
}
